import { useMemo } from 'react';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import {
    DetailTileCard,
    useIslandSection,
    useMagicIslandController,
    type DetailTile,
    type IslandMode,
} from '~/ui-rocco/pages/detail-shared';
import { useEnquiryOwnsIsland } from '../../hooks/use-enquiry-owns-island';
import { PRODUCT_TILE_GRID, PRODUCT_TILE_MEDIA } from '../product-tile-grid';

export interface RangeRowContentProps {
    tiles: DetailTile[];
    /** Range name — the island link reads "See more products in {rangeTitle}". */
    rangeTitle: string;
    /** Range directory URL; the island link is omitted without it. */
    rangeTo?: string | null;
}

/**
 * (NN) Other Products in this range — a 4-column (2 on mobile) grid of up to
 * eight range tiles inside the accordion. The Magic Island carries the "See more
 * products in {range}" link out to the range directory; the inline title, pills
 * and View Range CTA were dropped, so the accordion trigger is the only header.
 */
export const RangeRowContent = ({
    tiles,
    rangeTitle,
    rangeTo,
}: RangeRowContentProps) => {
    const { mobile } = useAppDisplayModeContext();
    const islandMounted = useMagicIslandController() !== null;
    const islandMode = useMemo<IslandMode>(
        () => ({
            kind: 'link',
            label: `See more products in ${rangeTitle}`,
            to: rangeTo ?? '',
        }),
        [rangeTitle, rangeTo],
    );
    // Desktop only: on mobile the persistent Enquire pill stays put rather than
    // being swapped for this internal nav link (which pulls the reader off the
    // product). Desktop runs the link through the first half of the page; past
    // half-scroll the island belongs to the enquiry fallback.
    const enquiryOwnsIsland = useEnquiryOwnsIsland();
    const islandRef = useIslandSection<HTMLDivElement>(islandMode, {
        enabled:
            !mobile && islandMounted && Boolean(rangeTo) && !enquiryOwnsIsland,
    });

    return (
        <div ref={islandRef} className="pb-[1.8rem] max-md:pb-4">
            <div className={PRODUCT_TILE_GRID}>
                {tiles.slice(0, 8).map((tile) => (
                    <DetailTileCard
                        key={tile.id}
                        tile={tile}
                        mediaClassName={PRODUCT_TILE_MEDIA}
                        // Browse tile scale: these sit inside an accordion row,
                        // where the 22px detail tile type reads oversized.
                        textClassName="md:text-[1rem] md:leading-[1.3]"
                    />
                ))}
            </div>
        </div>
    );
};
